import type { Tenant } from "@/types/entities";
import { PublishButton } from "@/components/custom/publish-button";

type Props = {
  tenant: Tenant;
};

export function Overview({ tenant }: Props) {
  const createdAt = formatDate(tenant.created_at);
  const updatedAt = formatDate(tenant.updated_at);

  return (
    <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      <header className="border-b border-border bg-muted/35 px-6 py-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">Tenant Overview</p>
            <h2 className="text-xl font-semibold tracking-tight">{tenant.name}</h2>
            <p className="text-sm text-muted-foreground">
              Core identity, routing, and lifecycle metadata for this tenant workspace.
            </p>
          </div>
          <PublishButton tenantSlug={tenant.slug} />
        </div>
      </header>

      <div className="space-y-5 p-6">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-lg border border-border bg-background px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Tenant ID</p>
            <p className="mt-1 text-sm font-semibold">#{tenant.id}</p>
          </div>
          <div className="rounded-lg border border-border bg-background px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Slug</p>
            <p className="mt-1 font-mono text-sm">{tenant.slug}</p>
          </div>
          <div className="rounded-lg border border-border bg-background px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Record Type</p>
            <p className="mt-1 text-sm font-semibold">Production Tenant</p>
          </div>
        </div>

        <dl className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-border bg-background px-4 py-3">
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Website URL</dt>
            <dd className="mt-1 text-sm">
              <a
                href={tenant.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary underline-offset-4 transition-colors hover:text-primary/80 hover:underline"
              >
                {tenant.website_url}
              </a>
            </dd>
          </div>
          <div className="rounded-lg border border-border bg-background px-4 py-3">
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Tenant Name</dt>
            <dd className="mt-1 text-sm font-medium">{tenant.name}</dd>
          </div>
          <div className="rounded-lg border border-border bg-background px-4 py-3">
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Created</dt>
            <dd className="mt-1 text-sm">{createdAt}</dd>
          </div>
          <div className="rounded-lg border border-border bg-background px-4 py-3">
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Last Updated</dt>
            <dd className="mt-1 text-sm">{updatedAt}</dd>
          </div>
        </dl>
      </div>
    </section>
  );
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
}
